/**
 * What each field of a record is called, for «Bitácora»'s sheet of one record (the owner,
 * 2026-10-12: «have it show all relevant details of that particular log event»).
 *
 * The fields are the registry's (`events/registry.ts` in shared), named in Spanish there for the
 * code; this is the word a person reads for each, in either language. Kept beside the domain's
 * other vocabulary rather than in the catalogue: one field, its two words side by side, the way
 * `dominio.ts` keeps the event types. Every field the registry declares has one, and a test says
 * so (`detalleEvento.test.ts`), so a field added to an event cannot reach that sheet as a slug.
 */

import { idiomaActual } from './idioma.js';

type Par = readonly [es: string, en: string];

const CAMPOS: Record<string, Par> = {
  abonoId: ['Abono', 'Client payment'],
  adelantoId: ['Adelanto', 'Advance'],
  adelantoMaximo: ['Adelantos que se le podían descontar', 'Advances that could be taken off'],
  aFiado: ['Se pasó a fiado', 'Moved to fiado'],
  aguinaldoPagado: ['Aguinaldo pagado', 'Aguinaldo paid'],
  anterior: ['Antes', 'Before'],
  apartadoAutorizadoPor: ['Autorizó tomar lo apartado', 'Allowed taking what was set aside'],
  aperturaSiguienteId: ['Apertura siguiente', 'Next opening'],
  autorizadoPor: ['Autorizado por', 'Authorised by'],
  bruto: ['Salario del período', 'Salary for the period'],
  cambio: ['Vuelto', 'Change given'],
  camion: ['Camión', 'Truck'],
  cantidad: ['Cantidad', 'Quantity'],
  cantidadBase: ['Cantidad', 'Quantity'],
  cantidadContada: ['Contado', 'Counted'],
  cantidadSistema: ['Según el sistema', 'The system said'],
  cargo: ['Cargo', 'Position'],
  categoria: ['Categoría', 'Category'],
  cedula: ['Cédula', 'ID number'],
  clave: ['Ajuste', 'Setting'],
  clienteId: ['Cliente', 'Client'],
  codigo: ['Código', 'Code'],
  codigosBarras: ['Códigos de barras', 'Barcodes'],
  compraId: ['Compra', 'Purchase'],
  compraSaldoId: ['Compra de saldo', 'Credit bought'],
  conInss: ['Paga INSS', 'Pays INSS'],
  conPrestaciones: ['Aguinaldo, vacaciones e indemnización', 'Aguinaldo, holiday and severance'],
  consumido: ['Lleva de lo abierto', 'Holds of what was opened'],
  contacto: ['Contacto', 'Contact'],
  contadoCordobas: ['Contado en córdobas', 'Counted in córdobas'],
  contadoDolares: ['Contado en dólares', 'Counted in dollars'],
  contadoEn: ['Contado a las', 'Counted at'],
  conteoCamion: ['Lo que volvió en el camión', 'Counted off the truck'],
  conteoCordobas: ['Contado en córdobas', 'Counted in córdobas'],
  conteoDolares: ['Contado en dólares', 'Counted in dollars'],
  conteoEfectivo: ['Efectivo entregado', 'Cash handed in'],
  conteoId: ['Conteo', 'Count'],
  cordobas: ['Córdobas', 'Córdobas'],
  corteId: ['Corte', 'Till count'],
  costo: ['Costo', 'Cost'],
  costoAdicional: ['Bolsas, etiquetas y mano de obra', 'Bags, labels and labour'],
  costoReferencia: ['Costo de referencia', 'Reference cost'],
  costoTotal: ['Costo', 'Cost'],
  costoUnitario: ['Costo por unidad', 'Cost per unit'],
  cuenta: ['Cuenta', 'Account'],
  cuentaDolares: ['Cuenta en dólares', 'Dollar account'],
  deGeneral: ['Puesto del efectivo general', 'Added from general cash'],
  depositoId: ['Depósito', 'Deposit'],
  descripcion: ['Descripción', 'Description'],
  descuento: ['Descuento', 'Discount'],
  desde: ['Desde', 'From'],
  destino: ['Destino', 'To'],
  destinos: ['Lo que salió', 'What it made'],
  devolucionId: ['Devolución', 'Return'],
  dias: ['Días trabajados', 'Days worked'],
  diasVacaciones: ['Días de vacaciones', 'Holiday days'],
  diaVisita: ['Día de visita', 'Visit day'],
  direccion: ['Dirección', 'Address'],
  dispositivoId: ['Equipo', 'Device'],
  documento: ['Factura', 'Invoice'],
  dolares: ['Dólares', 'Dollars'],
  donde: ['Dónde', 'Where'],
  empleadoId: ['Empleado', 'Employee'],
  envio: ['Envío', 'Delivery charge'],
  esMedicamento: ['Es medicamento', 'Medicine'],
  esperado: ['Esperado', 'Expected'],
  esPredeterminada: ['Lista predeterminada', 'Default list'],
  estado: ['Estado', 'Status'],
  eventoId: ['Conteo', 'Count'],
  facial: ['Saldo según la operadora', 'Balance per the carrier'],
  factor: ['Contiene', 'Contains'],
  faltaAutorizadaPor: ['Autorizó vender sin existencias', 'Allowed selling past the stock'],
  fecha: ['Fecha', 'Date'],
  fechaEntrega: ['Entrega', 'Delivery'],
  fechaIngreso: ['Fecha de ingreso', 'Start date'],
  flete: ['Flete', 'Freight'],
  fondoDolares: ['Fondo en dólares', 'Float in dollars'],
  fondoInicial: ['Fondo inicial', 'Opening float'],
  forma: ['Forma de pago', 'Paid by'],
  formaPago: ['Forma de pago', 'Paid by'],
  foto: ['Foto', 'Photo'],
  fotoCedula: ['Foto de la cédula', 'ID photo'],
  frecuencia: ['Cada cuánto se le paga', 'How often they are paid'],
  gastoFijo: ['Gasto fijo', 'Fixed expense'],
  gastoId: ['Gasto', 'Expense'],
  hacia: ['Hacia', 'To'],
  hasta: ['Hasta', 'To'],
  id: ['Gasto fijo', 'Fixed expense'],
  inatec: ['INATEC', 'INATEC'],
  incobrableId: ['Fiado perdido', 'Write-off'],
  indice: ['Línea', 'Line'],
  inicial: ['Primer conteo del efectivo general', 'First count of general cash'],
  inss: ['INSS', 'INSS'],
  inssNumero: ['Número de INSS', 'INSS number'],
  ir: ['IR', 'Income tax'],
  iva: ['IVA', 'VAT'],
  jornadaId: ['Ruta', 'Route'],
  lineas: ['Productos', 'Items'],
  limiteCredito: ['Límite de fiado', 'Fiado limit'],
  lleva: ['Lleva', 'Takes'],
  liquidacion: ['Cómo se devolvió el dinero', 'How the money came back'],
  listaAutorizadaPor: ['Autorizó la lista de precios', 'Allowed the price list'],
  listaId: ['Lista de precios', 'Price list'],
  lote: ['Lote', 'Batch'],
  loteId: ['Lote', 'Batch'],
  mermaCantidad: ['Merma', 'Lost'],
  moneda: ['Moneda', 'Currency'],
  monto: ['Monto', 'Amount'],
  montoFacial: ['Valor facial', 'Face value'],
  montoOriginal: ['Dólares recibidos', 'Dollars tendered'],
  motivo: ['Motivo', 'Reason'],
  motivoOverride: ['Por qué otro precio', 'Why another price'],
  movimientoId: ['Movimiento', 'Movement'],
  nombre: ['Nombre', 'Name'],
  nombreEn: ['Nombre en inglés', 'English name'],
  nota: ['Nota', 'Note'],
  notas: ['Notas', 'Notes'],
  ofertaId: ['Oferta', 'Offer'],
  numero: ['Número', 'Number'],
  operadora: ['Operadora', 'Carrier'],
  orden: ['Posición', 'Position'],
  origen: ['Origen', 'From'],
  origenCantidad: ['Cantidad abierta', 'Quantity opened'],
  origenProductoId: ['Producto abierto', 'Product opened'],
  otrosGravables: ['Horas extra, comisiones y bonos', 'Overtime, commissions and bonuses'],
  paga: ['Paga', 'Pays for'],
  pagoId: ['Pago', 'Payment'],
  pagos: ['Pagos', 'Payments'],
  pedidoId: ['Pedido', 'Order'],
  perecedero: ['Perecedero', 'Perishable'],
  periodo: ['Período', 'Period'],
  pinHash: ['PIN', 'PIN'],
  planillaId: ['Planilla', 'Payroll'],
  porDia: ['Se le paga por día trabajado', 'Paid by the day worked'],
  precio: ['Precio', 'Price'],
  precioLista: ['Precio de lista', 'List price'],
  precioRegular: ['Precio sin la oferta', 'Price without the offer'],
  precios: ['Precios', 'Prices'],
  precioUnitario: ['Precio', 'Price'],
  primerConteo: ['Primer conteo', 'First count'],
  primerConteoCordobas: ['Primer conteo en córdobas', 'First count in córdobas'],
  primerConteoDolares: ['Primer conteo en dólares', 'First count in dollars'],
  productoId: ['Producto', 'Product'],
  proveedorId: ['Proveedor', 'Supplier'],
  queda: ['Queda en la gaveta', 'Left in the drawer'],
  recargaId: ['Recarga', 'Top-up'],
  recibido: ['Llegó', 'Arrived'],
  recibidoPor: ['Lo recibió', 'Received by'],
  redondeo: ['Redondeo', 'Rounding'],
  reembolso: ['Cómo se devolvió el dinero', 'Refunded as'],
  reempaqueId: ['Reempaque', 'Repacking'],
  reemplazo: ['Corregido por', 'Corrected by'],
  reemplazoDespues: ['La corrección se anotó después', 'The correction was recorded after'],
  referencia: ['Referencia', 'Reference'],
  registroSanitario: ['Registro sanitario', 'Health registration'],
  reingresar: ['Vuelve al inventario', 'Back into stock'],
  reingresarStock: ['Vuelve al inventario', 'Back into stock'],
  relevoDe: ['Recibió la caja de', 'Took over from'],
  repartidorId: ['Repartidor', 'Driver'],
  respaldo: ['Copia de respaldo', 'Backup copy'],
  rol: ['Rol', 'Role'],
  salarioMensual: ['Salario mensual', 'Monthly salary'],
  salarioPendiente: ['Salario pendiente', 'Salary still owed'],
  saldo: ['Saldo', 'Balance'],
  saldoDolares: ['Saldo en dólares', 'Balance in dollars'],
  salidaId: ['Salida', 'Write-off'],
  sesionId: ['Caja', 'Till'],
  sinContar: ['Sin contar desde entonces', 'Not counted since'],
  soloSiHay: ['Solo si hay existencias', 'Only if in stock'],
  stockMinimo: ['Existencia mínima', 'Minimum stock'],
  subtotal: ['Subtotal', 'Subtotal'],
  tarifa: ['Lo acordado', 'Agreed pay'],
  tasaCambio: ['Tasa de cambio', 'Exchange rate'],
  telefono: ['Teléfono', 'Phone'],
  ticket: ['Ticket', 'Ticket'],
  tipo: ['Tipo', 'Type'],
  total: ['Total', 'Total'],
  transferenciaId: ['Transferencia', 'Transfer'],
  trasladoId: ['Traslado', 'Transfer'],
  ubicacion: ['Lugar', 'Location'],
  ubicacionRetorno: ['Lo que sobró volvió a', 'Leftovers went back to'],
  unidadBase: ['Unidad', 'Unit'],
  unidadVenta: ['Presentación', 'Pack size'],
  unidadVentaId: ['Presentación', 'Pack size'],
  usuarioId: ['Usuario', 'User'],
  vacacionesId: ['Vacaciones', 'Holiday'],
  vacacionesPagadas: ['Vacaciones pagadas', 'Holiday pay'],
  valor: ['Ahora', 'Now'],
  vencePago: ['Vence el pago', 'Payment due'],
  vencimiento: ['Vence', 'Expires'],
  ventaId: ['Venta', 'Sale'],
};

/** Where a field means something more particular in one kind of record. */
const EN_EL_REGISTRO: Record<string, Record<string, Par>> = {
  'banco.transferido': {
    desde: ['Sale de', 'From'],
    hacia: ['Llega a', 'To'],
    monto: ['Salió', 'Sent'],
    recibido: ['Llegó', 'Arrived'],
  },
  'semana.cuadrada': { desde: ['Desde el', 'From'], hasta: ['Hasta el', 'To'] },
  'dueno.retiro': { origen: ['Salió de', 'Taken from'], motivo: ['Para qué', 'What for'] },
  'dueno.aporte': { destino: ['Entró a', 'Put into'], motivo: ['Para qué', 'What for'] },
  'inventario.traslado': { origen: ['Desde', 'From'], destino: ['Hacia', 'To'], lineas: ['Lo que se trasladó', 'Moved'] },
  'inventario.salida': { motivo: ['Por qué salió', 'Why it left'] },
  'inventario.conteo': { lineas: ['Lo que se contó', 'Counted'] },
  'compra.devuelta': { lineas: ['Lo que se devolvió', 'Returned'] },
  'devolucion.registrada': { lineas: ['Lo que se devolvió', 'Returned'] },
  'planilla.corrida': {
    lineas: ['Empleados', 'Employees'],
    frecuencia: ['Para quienes se pagan', 'For those paid'],
    desde: ['Desde el', 'From'],
    hasta: ['Hasta el', 'To'],
  },
  // The aguinaldo paid on its own, and set right (2026-10-31).
  'aguinaldo.pagado': { lineas: ['Lo que se le pagó a cada quien', 'What each person was paid'] },
  'aguinaldo.corregido': {
    lineas: ['Lo que se le pagó a cada quien, corregido', 'What each person was paid, corrected'],
    pagoId: ['El pago que corrige', 'The payment it corrects'],
    nota: ['Por qué', 'Why'],
  },
  // Its lines as they should have been; none, voided (2026-10-31).
  'planilla.corregida': {
    lineas: ['Empleados, como quedaron', 'Employees, as corrected'],
    pagoId: ['El pago que cambia', 'The payment it changes'],
    nota: ['Por qué', 'Why'],
  },
  'empleado.liquidado': { motivo: ['Por qué se fue', 'Why they left'] },
  'empleado.vacaciones': { desde: ['Desde el', 'From'], hasta: ['Hasta el', 'To'], dias: ['Días descansados', 'Days rested'] },
  'caja.movimiento': { tipo: ['Movimiento', 'Movement'] },
  'caja.abierta': { esperado: ['Lo que dejó el turno anterior', 'Left by the shift before'] },
  'efectivo.arqueo': { esperado: ['Esperado, según la pantalla', 'Expected, as the screen showed'] },
  'jornada.abierta': { sesionId: ['Caja de la ruta', "The route's till"] },
  'jornada.liquidada': { conteoDolares: ['Dólares entregados', 'Dollars handed in'] },
  'compra.saldo_inicial': { monto: ['Se le debe', 'Owed to them'], fecha: ['Fecha de la factura', 'Invoice date'] },
  'compra.cantidad_corregida': { cantidadBase: ['Lo que de verdad llegó', 'What really came'] },
  'compra.forma_pago_corregida': { formaPago: ['Cómo se pagó de verdad', 'How it was really paid'] },
  'inventario.costo_corregido': { costoUnitario: ['Costo nuevo', 'New cost'] },
  // The lines alone: the delivery has its own row beside it (2026-10-12).
  'pedido.tomado': { total: ['Productos', 'Goods'] },
  'pedido.actualizado': { total: ['Productos', 'Goods'] },
  'cliente.saldo_inicial': { monto: ['Debía', 'Owed'] },
  'faltante.explicado': { eventoId: ['El conteo que lo encontró', 'The count that found it'] },
  'caja.apertura_corregida': {
    eventoId: ['La apertura', 'The opening'],
    corteId: ['Su corte', 'Its till count'],
    fondoInicial: ['Lo que de verdad había', 'What was really there'],
    fondoDolares: ['Dólares que de verdad había', 'Dollars really there'],
  },
  'caja.corte_corregido': {
    eventoId: ['El corte', 'The till count'],
    aperturaSiguienteId: ['La apertura siguiente', 'The next opening'],
    conteoCordobas: ['Lo que de verdad se contó', 'What was really counted'],
    conteoDolares: ['Dólares que de verdad se contaron', 'Dollars really counted'],
  },
  'recarga.saldo_comprado': { costo: ['Lo que costó', 'What it cost'] },
  'cuenta.conciliada': { saldo: ['Saldo según el estado de cuenta', 'Balance per the statement'] },
};

/** The words for values the catalogue does not already have. */
const VALORES: Record<string, Par> = {
  'tipo.retiro': ['Retiro', 'Taken out'],
  'tipo.ingreso': ['Ingreso', 'Put in'],
  'estado.confirmado': ['Confirmado', 'Confirmed'],
  'estado.pendiente': ['Por confirmar', 'To be confirmed'],
  'liquidacion.descontar': ['Rebajado de lo que se le debía', 'Taken off what was owed to them'],
  'reembolso.credito': ['Rebajado de lo que debe', 'Taken off what they owe'],
};

const enIngles = () => idiomaActual() === 'en';

/** A field's name, as a record of `tipo` uses it, in the language on screen. */
export function etiquetaCampo(campo: string, tipo?: string): string {
  const par = (tipo ? EN_EL_REGISTRO[tipo]?.[campo] : undefined) ?? CAMPOS[campo];
  if (!par) return campo;
  return enIngles() ? par[1] : par[0];
}

/** A value of `campo` this file names (`tipo.retiro`), or null where the catalogue does. */
export function etiquetaValor(campo: string, valor: string): string | null {
  const par = VALORES[`${campo}.${valor}`];
  return par ? (enIngles() ? par[1] : par[0]) : null;
}

/** Whether a field has its words here, for the test that holds every field of the registry to it. */
export const tieneEtiqueta = (campo: string): boolean => Object.prototype.hasOwnProperty.call(CAMPOS, campo);
